/**
 * Near Me API service. Searches are POSTs so coordinates never sit in a URL.
 */

import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './api';
import type { ApiError } from '@/types';
import type {
  Accepted,
  AttendanceResponse,
  DuplicateVenue,
  EventDetail,
  EventSearchParams,
  EventSearchResponse,
  EventSubmission,
  GeocodeResult,
  HomeLocation,
  ManagePatch,
  ModerationQueue,
  ModerationResult,
  MyClaim,
  RevokeResult,
  VenueManagerRow,
  MyVenue,
  SeedApproveBody,
  SeedApproveResult,
  SeedList,
  WaitingCounts,
  SeriesIn,
  SeriesOut,
  VenueManage,
  PlayHereResponse,
  ReportReason,
  VenueDetail,
  VenueSearchParams,
  VenueSearchResponse,
  VenueSubmission,
} from '@/types/nearMe';
import { roundCoord } from '@/utils/geolocation';

const BASE = '/api/near-me';
const ADMIN = '/api/admin/near-me';
const id = (value: string) => encodeURIComponent(value);

export function searchVenues(params: VenueSearchParams) {
  return apiPost<VenueSearchResponse>(`${BASE}/venues/search`, {
    ...params, lat: roundCoord(params.lat), lon: roundCoord(params.lon),
  });
}

export function searchEvents(params: EventSearchParams) {
  return apiPost<EventSearchResponse>(`${BASE}/events/search`, {
    ...params, lat: roundCoord(params.lat), lon: roundCoord(params.lon),
  });
}

export function geocodePlace(query: string) {
  return apiPost<GeocodeResult>(`${BASE}/geocode`, { query });
}

export function getVenue(venueId: string) {
  return apiGet<VenueDetail>(`${BASE}/venues/${id(venueId)}`);
}

export function getEvent(eventId: string) {
  return apiGet<EventDetail>(`${BASE}/events/${id(eventId)}`);
}

export function setPlayHere(venueId: string, playing: boolean) {
  const url = `${BASE}/venues/${id(venueId)}/players/me`;
  return playing ? apiPut<PlayHereResponse>(url) : apiDelete<PlayHereResponse>(url);
}

export function setAttendance(eventId: string, going: boolean, isPublic = false) {
  const url = `${BASE}/events/${id(eventId)}/attendance`;
  return going ? apiPut<AttendanceResponse>(url, { isPublic }) : apiDelete<AttendanceResponse>(url);
}

export function reportVenue(venueId: string, reason: ReportReason, note?: string) {
  return apiPost<Accepted>(`${BASE}/venues/${id(venueId)}/reports`, { reason, note: note || null });
}

export function submitVenue(body: VenueSubmission) {
  return apiPost<Accepted>(`${BASE}/venues`, body);
}

export function submitEvent(body: EventSubmission) {
  return apiPost<Accepted>(`${BASE}/events`, body);
}

export function submitIcsFeed(venueId: string, url: string) {
  return apiPost<Accepted>(`${BASE}/venues/${id(venueId)}/ics-feed`, { url });
}

export function getHome() {
  return apiGet<HomeLocation>(`${BASE}/me/home`);
}

export function setHome(query: string) {
  return apiPut<HomeLocation>(`${BASE}/me/home`, { query });
}

export function clearHome() {
  return apiDelete<void>(`${BASE}/me/home`);
}

export function getMyVenues() {
  return apiGet<MyVenue[]>(`${BASE}/me/venues`);
}

export function duplicateFrom(error?: ApiError): DuplicateVenue | null {
  const detail = (error?.details as { detail?: unknown } | undefined)?.detail as DuplicateVenue | undefined;
  return detail && detail.code === 'possible_duplicate' ? detail : null;
}

export function getWaitingCounts() {
  return apiGet<WaitingCounts>(`${ADMIN}/waiting`);
}

export function getModerationQueue() {
  return apiGet<ModerationQueue>(`${ADMIN}/queue`);
}

export function moderateVenue(venueId: string, decision: 'approve' | 'reject') {
  return apiPost<ModerationResult>(`${ADMIN}/venues/${id(venueId)}/moderate`, { decision });
}

export function moderateEvent(eventId: string, decision: 'approve' | 'reject') {
  return apiPost<ModerationResult>(`${ADMIN}/events/${id(eventId)}/moderate`, { decision });
}

/** The admin approves the exact URL they were shown; the server returns 409 if it changed. */
export function moderateFeed(venueId: string, decision: 'approve' | 'reject', icsUrl: string) {
  return apiPost<ModerationResult>(`${ADMIN}/venues/${id(venueId)}/feed/moderate`, { decision, icsUrl });
}

export function resolveReport(reportId: string, action: 'dismiss' | 'hide_venue') {
  return apiPost<ModerationResult>(`${ADMIN}/reports/${id(reportId)}/resolve`, { action });
}

export function setVenueHidden(venueId: string, hidden: boolean) {
  return apiPost<ModerationResult>(`${ADMIN}/venues/${id(venueId)}/visibility`, { hidden });
}

/* ---------------------------------------------------------------- N2: claims, managers, series */

export function claimVenue(venueId: string, note: string) {
  return apiPost<{ id: string; status: 'pending' }>(`${BASE}/venues/${id(venueId)}/claim`, { note: note.trim() });
}

export function getMyClaims() {
  return apiGet<{ claims: MyClaim[] }>(`${BASE}/me/claims`);
}

export function getVenueManage(venueId: string) {
  return apiGet<VenueManage>(`${BASE}/venues/${id(venueId)}/manage`);
}

export function patchVenueManage(venueId: string, body: ManagePatch) {
  return apiPatch<VenueManage>(`${BASE}/venues/${id(venueId)}/manage`, body);
}

export function createSeries(venueId: string, body: SeriesIn) {
  return apiPost<SeriesOut>(`${BASE}/venues/${id(venueId)}/series`, body);
}

export function updateSeries(seriesId: string, body: Partial<SeriesIn>) {
  return apiPatch<SeriesOut>(`${BASE}/series/${id(seriesId)}`, body);
}

export function deleteSeries(seriesId: string) {
  return apiDelete<void>(`${BASE}/series/${id(seriesId)}`);
}

export function skipSeriesDate(seriesId: string, date: string) {
  return apiPost<SeriesOut>(`${BASE}/series/${id(seriesId)}/skips`, { date });
}

export function unskipSeriesDate(seriesId: string, date: string) {
  return apiDelete<SeriesOut>(`${BASE}/series/${id(seriesId)}/skips/${id(date)}`);
}

export function deleteEvent(eventId: string) {
  return apiDelete<void>(`${BASE}/events/${id(eventId)}`);
}

/* ---------------------------------------------------------------- N2: admin additions */

export function moderateSeries(seriesId: string, decision: 'approve' | 'reject') {
  return apiPost<ModerationResult>(`${ADMIN}/series/${id(seriesId)}/moderate`, { decision });
}

export function moderateClaim(claimId: string, decision: 'approve' | 'reject', role?: 'owner' | 'organiser') {
  return apiPost<ModerationResult>(`${ADMIN}/claims/${id(claimId)}/moderate`, { decision, ...(role ? { role } : {}) });
}

export function listSeedCandidates(status: 'pending' | 'approved' | 'rejected' = 'pending', limit = 100) {
  return apiGet<SeedList>(`${ADMIN}/seed?status=${status}&limit=${limit}`);
}

export function approveSeedCandidate(candidateId: string, body: SeedApproveBody) {
  return apiPost<SeedApproveResult>(`${ADMIN}/seed/${id(candidateId)}/approve`, body);
}

export function rejectSeedCandidate(candidateId: string) {
  return apiPost<ModerationResult>(`${ADMIN}/seed/${id(candidateId)}/reject`);
}

export function listVenueManagers(venueId: string) {
  return apiGet<{ managers: VenueManagerRow[] }>(`${ADMIN}/venues/${id(venueId)}/managers`);
}

/** Withdraw an approved manager. The server answers 409 if they are no longer approved. */
export function revokeManager(managerId: string, body: { unpublishContent?: boolean; reason?: string }) {
  return apiPost<RevokeResult>(`${ADMIN}/managers/${id(managerId)}/revoke`, {
    unpublishContent: Boolean(body.unpublishContent),
    ...(body.reason?.trim() ? { reason: body.reason.trim() } : {}),
  });
}

/** The server's structured error body: `{detail: {code, ...}}`. */
export function errorCode(error?: ApiError): string | null {
  const detail = (error?.details as { detail?: { code?: unknown } } | undefined)?.detail;
  return typeof detail === 'object' && detail && typeof detail.code === 'string' ? detail.code : null;
}
